import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { Slide } from '@/framework/types';
import {
  LinearFlow,
  FlowDiagram,
  AuditTeeDiagram,
  HypothesisBranches,
  HubSpoke,
  ChainDiagram,
  Glyph,
  type Glyph as GlyphKind,
} from './diagrams';

/* ══════════════════════════════════════════════════════════════════════
   Shared challenge layout
   ══════════════════════════════════════════════════════════════════════ */

type StreamingVerdict = 'yes' | 'no';

type ChallengeSlideProps = {
  pillar: string;
  challengeIndex: string;
  title: string;
  titleEm?: string;
  thesis: ReactNode;
  isGap?: boolean;
  streamingProblem: StreamingVerdict;
  verdictNote?: string;
  diagram: ReactNode;
};

function ChallengeSlide({
  pillar, challengeIndex, title, titleEm, thesis, isGap, streamingProblem, verdictNote, diagram,
}: ChallengeSlideProps) {
  return (
    <div className="slide-body challenge-body">
      <div className="challenge-head">
        <div className="challenge-eyebrow">
          <span className={`eyebrow-tag${isGap ? ' is-gap' : ''}`}>{pillar}</span>
          <span className="eyebrow-sep">·</span>
          <span className="eyebrow-mute">Challenge {challengeIndex}</span>
          <span className={`streaming-verdict streaming-verdict--${streamingProblem}`}>
            Streaming problem · <strong>{streamingProblem === 'yes' ? 'Yes' : 'No'}</strong>
            {verdictNote && <span className="verdict-note">{verdictNote}</span>}
          </span>
        </div>
        <h2 className="challenge-title">
          {title}
          {titleEm && <em> · {titleEm}</em>}
        </h2>
        <p className="challenge-thesis">{thesis}</p>
      </div>
      <div className="challenge-diagram">{diagram}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Slide 1 — Title
   ══════════════════════════════════════════════════════════════════════ */

const TitleSlide = () => (
  <div className="slide-body s1-body">
    <div>
      <div className="s1-eyebrow">Streaming the Future · Builder Evening · SF</div>
      <h1 className="sn-heading-hero s1-title">
        Streaming as the <em>Backbone</em> of Autonomous AI Agents
      </h1>
    </div>
    <dl className="s1-meta">
      <dt>Speaker</dt><dd>Sijie Guo</dd>
      <dt>Role</dt><dd>Co-founder &amp; CEO, StreamNative</dd>
      <dt>Credential</dt><dd>Co-creator of Apache Pulsar</dd>
      <dt>Hosts</dt><dd>Aiven · StreamNative · RisingWave</dd>
    </dl>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 2 — About StreamNative
   ══════════════════════════════════════════════════════════════════════ */

const AboutSlide = () => (
  <div className="slide-body about-body">
    <div className="about-head">
      <img src="/assets/logo-blue-black.svg" alt="StreamNative" className="about-logo" />
      <p className="about-tag">The Lakestream Platform</p>
    </div>
    <p className="about-lead">
      The <em>Lakestream Platform</em> for <em>Apache Pulsar</em> and <em>Apache Kafka</em>,
      engineered by the original creators of Apache Pulsar.
    </p>
    <div className="about-grid">
      <div className="about-card">
        <span className="about-card-num">01</span>
        <h3>Ursa engine</h3>
        <p>Object-storage-native streaming. Stream-as-table. Built for elastic, multi-tenant scale at lakehouse cost.</p>
      </div>
      <div className="about-card">
        <span className="about-card-num">02</span>
        <h3>Bring-Your-Own-Cloud</h3>
        <p>Data and compute stay in your VPC. Managed control plane, customer data plane.</p>
      </div>
      <div className="about-card">
        <span className="about-card-num">03</span>
        <h3>Agent-ready</h3>
        <p>Orca Agent Engine on top of streaming — Runtime, Registry, MCP gateway, versioned memory.</p>
      </div>
      <div className="about-card">
        <span className="about-card-num">04</span>
        <h3>Open by default</h3>
        <p>SQL, Kafka API, Iceberg, Pulsar, MCP, A2A. Standards in; no lock-in out.</p>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 3 — The Gap
   ══════════════════════════════════════════════════════════════════════ */

const GapSlide = () => (
  <div className="slide-body s2-body">
    <p className="s2-stack">
      Every enterprise wants agentic AI in production. <em>Few are getting there.</em>
      <br /><br />
      The gap isn&apos;t <span className="strike">model quality</span>.
      <br />
      The gap is <em>infrastructure</em> <span className="paren">(&ldquo;harness&rdquo;)</span>.
    </p>
    <div className="s2-meta">
      <div className="s2-meta-cell"><span className="num">9</span>distinct infra challenges they hit</div>
      <div className="s2-meta-cell"><span className="num">3</span>pillars they reduce to</div>
      <div className="s2-meta-cell"><span className="num">1</span>connective substrate underneath</div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 4 — Three Pillars
   ══════════════════════════════════════════════════════════════════════ */

type Pillar = { key: 'context' | 'trust' | 'coordination'; index: string; name: string; promise: string; challenges: { num: string; label: string }[] };

const PILLARS: Pillar[] = [
  {
    key: 'context', index: 'Pillar 01', name: 'Context',
    promise: 'Give the agent the right live picture to reason over.',
    challenges: [
      { num: '01', label: 'Context building & maintenance' },
      { num: '02', label: 'Context querying' },
      { num: '03', label: 'Memory, session & state' },
    ],
  },
  {
    key: 'trust', index: 'Pillar 02', name: 'Trust',
    promise: 'Authorized, governed, observable, replayable — every action.',
    challenges: [
      { num: '04', label: 'Auditing' },
      { num: '05', label: 'Replay & validation' },
      { num: '06', label: 'Governance' },
      { num: '07', label: 'Auth (inbound & outbound)' },
    ],
  },
  {
    key: 'coordination', index: 'Pillar 03', name: 'Coordination',
    promise: 'Run agents, route data, let them talk to each other.',
    challenges: [
      { num: '08', label: 'Dynamic routing' },
      { num: '09', label: 'Multi-agent coordination' },
    ],
  },
];

const FrameworkSlide = () => (
  <div className="slide-body s3-body">
    <p className="s3-lead">
      Every enterprise agent platform has to deliver on <em>three things</em>.
    </p>
    <div className="pillars">
      {PILLARS.map(p => (
        <article key={p.key} className="pillar" data-key={p.key}>
          <div className="pillar-num">{p.index}</div>
          <h3 className="pillar-name">{p.name}</h3>
          <p className="pillar-promise">{p.promise}</p>
          <ul className="pillar-challenges">
            {p.challenges.map(c => <li key={c.num} data-num={c.num}>{c.label}</li>)}
          </ul>
        </article>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 5 — Thesis bars
   ══════════════════════════════════════════════════════════════════════ */

const Bar = ({ label, pct, w }: { label: string; pct: string; w: number }) => (
  <>
    <div className="lbl">{label}</div>
    <div className="track">
      <div className="fill" style={{ '--w': w } as React.CSSProperties}>
        <span className="fill-label">Streaming</span>
      </div>
      <span className="gap-label">Gap</span>
    </div>
    <div className="pct">{pct}</div>
  </>
);

const ThesisSlide = () => (
  <div className="slide-body s4-body">
    <h2 className="s4-thesis">Streaming covers <em>most</em> of each pillar.</h2>
    <div className="bars-legend">
      <span className="legend-item legend-streaming">
        <span className="legend-swatch" /> Streaming covers
      </span>
      <span className="legend-item legend-gap">
        <span className="legend-swatch" /> Gap (partners + Agent Engine)
      </span>
    </div>
    <div className="bars">
      <Bar label="Context" pct="~80%" w={0.8} />
      <Bar label="Trust" pct="~90%" w={0.9} />
      <Bar label="Coordination" pct="~80%" w={0.8} />
    </div>
    <p className="s4-foot">
      The gap is closed on slide sixteen — partners + Agent Engine.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slides 6–14 — Challenge slides
   ══════════════════════════════════════════════════════════════════════ */

const C1_Building = () => (
  <ChallengeSlide
    pillar="Pillar 01 · Context" challengeIndex="01 / 09"
    streamingProblem="yes"
    title="Building & maintenance" titleEm="streaming ETL, multi-modal"
    thesis={<>Sources stream through a single substrate and fan out to vector, OLAP, and feature stores in real time. Multi-modal — text, images, audio — moves through the same pipe.</>}
    diagram={
      <LinearFlow
        leftLabel="Multi-modal sources"
        rightLabel="Context stores"
        centerLabel="single substrate"
        left={[
          { label: 'CRM', glyph: 'crm' },
          { label: 'Docs', glyph: 'doc' },
          { label: 'Logs', glyph: 'log' },
          { label: 'Images', glyph: 'image' },
          { label: 'Audio', glyph: 'audio' },
        ]}
        center={{ label: 'Streaming', sub: 'connect · fan-out', glyph: 'stream' }}
        right={[
          { label: 'Vector DB', glyph: 'vector' },
          { label: 'OLAP', glyph: 'olap' },
          { label: 'Feature store', glyph: 'feature' },
          { label: 'Object store', glyph: 'db' },
        ]}
      />
    }
  />
);

const C2_Querying = () => (
  <ChallengeSlide
    pillar="Pillar 01 · Context" challengeIndex="02 / 09"
    isGap
    streamingProblem="no" verdictNote="Partner ecosystem closes it"
    title="Querying" titleEm="vector + SQL at request time"
    thesis={<>Retrieval at request time is a query problem, not a streaming problem. <strong>RisingWave</strong> turns the stream into queryable state — the partner ecosystem closes this gap natively.</>}
    diagram={
      <FlowDiagram
        leftLabel="Agent request"
        rightLabel="Materialized state"
        left={[
          { label: 'Agent', glyph: 'agent' },
          { label: 'Retrieval', glyph: 'doc' },
        ]}
        center={{ label: 'RisingWave', sub: 'partner ecosystem', glyph: 'gateway' }}
        right={[
          { label: 'Vector index', glyph: 'vector' },
          { label: 'Streaming SQL views', glyph: 'olap' },
          { label: 'Materialized tables', glyph: 'db' },
        ]}
      />
    }
  />
);

const C3_Memory = () => (
  <ChallengeSlide
    pillar="Pillar 01 · Context" challengeIndex="03 / 09"
    streamingProblem="yes"
    title="Memory, session & state" titleEm="the log is versioned & branchable by construction"
    thesis={<>Agents explore alternative hypotheses — try plan A, abandon, try plan B. That demands <strong>branchable</strong> state. Append-only streaming logs are versioned by construction; branching a session is a pointer copy, not a database copy.</>}
    diagram={<HypothesisBranches tone="light" />}
  />
);

const C4_Auditing = () => (
  <ChallengeSlide
    pillar="Pillar 02 · Trust" challengeIndex="04 / 09"
    streamingProblem="yes"
    title="Auditing" titleEm="LLMs are opaque — capture every I/O"
    thesis={<>The agent loops with LLMs and tools. Every exchange — prompts, retrievals, tool calls, responses — is tee&apos;d into a streaming <strong>audit log</strong> with full payloads. Streaming serves the log; tiered storage handles the volume.</>}
    diagram={<AuditTeeDiagram tone="light" />}
  />
);

const C5_Replay = () => (
  <ChallengeSlide
    pillar="Pillar 02 · Trust" challengeIndex="05 / 09"
    streamingProblem="yes"
    title="Replay & validation" titleEm="the audit log doubles as your test corpus"
    thesis={<>Same log → replay engine → new model/prompt → diff outputs. Compliance evidence and regression fixtures from one piece of infrastructure.</>}
    diagram={
      <ChainDiagram
        tone="light"
        steps={[
          { label: 'Audit log', sub: "yesterday's traffic", glyph: 'log' },
          { label: 'Replay engine', sub: 're-runs events', glyph: 'replay', tone: 'primary' },
          { label: 'New version', sub: 'changed prompt/model', glyph: 'agent' },
          { label: 'Diff', sub: 'regressions surface', glyph: 'audit' },
        ]}
      />
    }
  />
);

const C6_Governance = () => (
  <ChallengeSlide
    pillar="Pillar 02 · Trust" challengeIndex="06 / 09"
    streamingProblem="no" verdictNote="But the right enforcement layer"
    title="Governance" titleEm="built into every layer, enforced at the stream"
    thesis={<>Governance isn&apos;t a separate product — it&apos;s built into every layer that touches data. Streaming is the connective layer everything flows through, which makes it the natural chokepoint.</>}
    diagram={
      <LinearFlow
        leftLabel="Sources" rightLabel="Consumers"
        centerLabel="governance chokepoint"
        left={[
          { label: 'CRM', glyph: 'crm' },
          { label: 'Logs', glyph: 'log' },
          { label: 'Docs', glyph: 'doc' },
          { label: 'Lake', glyph: 'lake' },
        ]}
        center={{ label: 'Data Streaming', sub: 'RBAC · audit · lineage', glyph: 'stream' }}
        right={[
          { label: 'Agents', glyph: 'agent' },
          { label: 'Tools', glyph: 'tool' },
          { label: 'Analytics', glyph: 'olap' },
        ]}
      />
    }
  />
);

const C7_Auth = () => (
  <ChallengeSlide
    pillar="Pillar 02 · Trust" challengeIndex="07 / 09"
    isGap
    streamingProblem="no" verdictNote="Gateway concern, not a topic"
    title="Auth" titleEm="centralize at the gateway"
    thesis={<>Tool calls and model calls need centralized inbound and outbound auth. That&apos;s an <strong>AI Gateway</strong> concern, not a streaming one — Agent Engine fills the gap on slide fifteen.</>}
    diagram={
      <LinearFlow
        leftLabel="Callers" rightLabel="Destinations"
        left={[
          { label: 'Internal agents', glyph: 'agent' },
          { label: 'Partners', glyph: 'crm' },
          { label: 'Users', glyph: 'doc' },
        ]}
        center={{ label: 'AI Gateway', sub: 'auth · rate · audit', glyph: 'mcp' }}
        right={[
          { label: 'LLM providers', glyph: 'llm' },
          { label: 'Tools', glyph: 'wrench' },
          { label: 'Other agents', glyph: 'agent' },
        ]}
      />
    }
  />
);

const C8_Routing = () => (
  <ChallengeSlide
    pillar="Pillar 03 · Coordination" challengeIndex="08 / 09"
    streamingProblem="yes"
    title="Dynamic routing" titleEm="agents for smart actions, functions for deterministic"
    thesis={<>Not every request needs an LLM. Inspect the event, send <strong>ambiguous / reasoning</strong> work to agents (LLM-powered, smart actions), and <strong>high-throughput / deterministic</strong> work to functions (μs latency, code-defined). The Router itself can be a <strong>Function or an Agent</strong> — same substrate, two compute models.</>}
    diagram={
      <LinearFlow
        leftLabel="Mixed traffic" rightLabel="Right tool for the job"
        left={[
          { label: 'Events', glyph: 'stream' },
          { label: 'Requests', glyph: 'doc' },
        ]}
        center={{ label: 'Router', sub: 'Function · or · Agent', glyph: 'router' }}
        right={[
          { label: 'Agents · LLM', sub: 'smart actions', glyph: 'llm' },
          { label: 'Functions · code', sub: 'deterministic · μs', glyph: 'function' },
          { label: 'Cached / rules', sub: 'pre-computed', glyph: 'cache' },
          { label: 'Drop', sub: 'no-op path', glyph: 'rules' },
        ]}
      />
    }
  />
);

const C9_MultiAgent = () => (
  <ChallengeSlide
    pillar="Pillar 03 · Coordination" challengeIndex="09 / 09"
    streamingProblem="yes"
    title="Multi-agent coordination" titleEm="from point-to-point chaos to one event bus"
    thesis={<>Direct agent-to-agent integrations explode. <strong>Each agent connects once</strong> to a shared event bus; agents publish domain events and subscribe to what they need.</>}
    diagram={
      <HubSpoke
        tone="light"
        hub={{ label: 'Event bus', sub: 'pub / sub\ndurable · ordered', glyph: 'stream' }}
        spokes={[
          { label: 'Support agent',  glyph: 'agent', angle: -150, eventLabel: 'UserIssueResolved' },
          { label: 'Sales agent',    glyph: 'agent', angle: -90,  eventLabel: 'UpsellOpportunity' },
          { label: 'Analytics agent',glyph: 'agent', angle: -30,  eventLabel: 'TrendDetected' },
          { label: 'DevOps agent',   glyph: 'agent', angle:  30,  eventLabel: 'IncidentOpened' },
          { label: 'Product agent',  glyph: 'agent', angle:  90,  eventLabel: 'FeatureRequest' },
          { label: 'Finance agent',  glyph: 'agent', angle:  150, eventLabel: 'InvoiceFlagged' },
        ]}
      />
    }
  />
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 15 — Honest caveat (4 gaps)
   ══════════════════════════════════════════════════════════════════════ */

type Gap = { num: string; name: string; desc: string; glyph: GlyphKind; closer: string };
const GAPS: Gap[] = [
  { num: 'Gap 01', name: 'Querying',           glyph: 'vector', closer: 'RisingWave',                 desc: 'Vector + SQL retrieval at request time. Streaming is the wrong shape for fan-in lookup.' },
  { num: 'Gap 02', name: 'Auth centralization', glyph: 'mcp',    closer: 'MCP & AI Gateway',           desc: 'Inbound/outbound auth for tool and model calls. Needs a dedicated gateway, not a topic.' },
  { num: 'Gap 03', name: 'Agent runtime',      glyph: 'agent',  closer: 'Event-driven Runtime',       desc: 'Long-running, durable execution with framework harnesses for Agents, Functions, Connectors.' },
  { num: 'Gap 04', name: 'Versioned storage',  glyph: 'log',    closer: 'Versioned, Branchable Storage', desc: 'The log is already versioned. The gap is exposing it as a usable API for memory & sessions.' },
];

const CaveatSlide = () => (
  <div className="slide-body s8-body">
    <div>
      <p className="s8-eyebrow">Honest caveat</p>
      <h2 className="s8-lead">Streaming gets you <em>~85% of the way.</em> The gaps are real.</h2>
    </div>
    <div className="gaps">
      {GAPS.map(g => (
        <article key={g.num} className="gap-card">
          <div className="gap-card-top">
            <span className="gap-card-icon"><Glyph kind={g.glyph} size={32} accent="#0070D9" /></span>
            <span className="gap-card-num">{g.num}</span>
          </div>
          <h3 className="gap-name">{g.name}</h3>
          <p className="gap-desc">{g.desc}</p>
          <div className="gap-card-foot">
            <span className="gap-card-foot-label">Closed by</span>
            <span className="gap-card-foot-value">{g.closer}</span>
          </div>
        </article>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 16 — Partners + Agent Engine close the gaps
   ══════════════════════════════════════════════════════════════════════ */

type Closer = { gap: string; owner: 'partner' | 'sn'; ownerLabel: string; name: ReactNode; desc: string };
const CLOSERS: Closer[] = [
  {
    gap: 'Gap 01 · Querying',
    owner: 'partner', ownerLabel: 'Partner ecosystem',
    name: 'RisingWave / Flink SQL',
    desc: 'Streaming SQL turns the stream into queryable state — vector and structured retrieval at request time.',
  },
  {
    gap: 'Gap 02 · Auth + protocols',
    owner: 'sn', ownerLabel: 'Orca Agent Engine',
    name: 'MCP & AI Gateway',
    desc: 'One boundary for tool and model calls. Auth, rate limits, schemas, and audit centralized.',
  },
  {
    gap: 'Gap 03 · Agent runtime',
    owner: 'sn', ownerLabel: 'Orca Agent Engine',
    name: 'Event-driven Runtime',
    desc: 'Hosts long-running Agents, Functions, and Connectors — Google ADK, OpenAI Agents SDK, custom harnesses.',
  },
  {
    gap: 'Gap 04 · Memory & state',
    owner: 'sn', ownerLabel: 'Orca Agent Engine',
    name: 'Versioned, Branchable Storage',
    desc: 'Productizes the log — versioned by construction, branchable for the cost of a pointer copy.',
  },
];

const ClosersSlide = () => (
  <div className="slide-body s9-body">
    <h2 className="s9-lead">
      Partners + Agent Engine — <em>four closers for four gaps.</em>
    </h2>
    <div className="elements">
      {CLOSERS.map((c, i) => (
        <div key={i} className={`element-row owner-${c.owner}`}>
          <div className="gap-was">{c.gap}</div>
          <div className="owner-pill">{c.ownerLabel}</div>
          <div className="elem-name">{c.name}</div>
          <div className="elem-desc">{c.desc}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 17 — Platform (merged from old 17 + 18; no image)
   ══════════════════════════════════════════════════════════════════════ */

type Layer = {
  tone: 'primary' | 'secondary';
  tag: string;
  name: string;
  parts: string[];
  interop: { label: string; values: string[] };
};

const LAYERS: Layer[] = [
  {
    tone: 'primary',
    tag: 'Layer 01',
    name: 'Orca Agent Engine',
    parts: ['Runtime', 'Registry', 'MCP & AI Gateway', 'Memory & Sessions'],
    interop: { label: 'Interop', values: ['MCP', 'A2A', 'OTEL'] },
  },
  {
    tone: 'secondary',
    tag: 'Layer 02',
    name: 'Data Streaming',
    parts: ['Connect', 'Stream', 'Query'],
    interop: { label: 'Interop', values: ['Kafka', 'Pulsar', 'SQL', 'Iceberg'] },
  },
];

const GOVERNANCE_PILLS = ['BYOC', 'RBAC', 'Audit logs', 'Observability', 'Inbound / Outbound auth', 'Lineage'];

const PlatformSlide = () => (
  <div className="slide-body s10-body">
    <h2 className="s10-lead">
      Data Streaming + Agent Engine — <em>secure and governed by construction.</em>
    </h2>
    <div className="platform-stack">
      {LAYERS.map(l => (
        <section key={l.name} className="platform-layer" data-tone={l.tone}>
          <div className="platform-layer-head">
            <span className="layer-tag">{l.tag}</span>
            <span className="layer-name">{l.name}</span>
          </div>
          <div className="layer-parts">
            {l.parts.map(p => <span key={p}>{p}</span>)}
          </div>
          <div className="layer-interop">
            <span className="interop-label">{l.interop.label}</span>
            <div className="interop-values">
              {l.interop.values.map(v => <span key={v}>{v}</span>)}
            </div>
          </div>
        </section>
      ))}
      <section className="platform-governance">
        <div className="platform-layer-head">
          <span className="layer-tag">Cross-cutting · built into every layer</span>
          <span className="layer-name">Governance</span>
        </div>
        <div className="gov-pills">
          {GOVERNANCE_PILLS.map(g => <span key={g}>{g}</span>)}
        </div>
      </section>
    </div>
    <p className="s10-foot">Streaming plays the critical role — the substrate everything else rides on.</p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 18 — Mental model
   ══════════════════════════════════════════════════════════════════════ */

const MantraSlide = () => (
  <div className="slide-body s11-body">
    <div className="mantra">
      <span className="w1">Context</span>
      <span className="sep">·</span>
      <span className="w2">Trust</span>
      <span className="sep">·</span>
      <span className="w3">Coordination</span>
    </div>
    <p className="mantra-foot">
      If your agent platform delivers on all three — at enterprise scale, with audit and replay — you almost
      certainly built it on a <strong>streaming substrate</strong>. Whether you knew it or not.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 19 — Private Preview + QR
   ══════════════════════════════════════════════════════════════════════ */

const EARLY_ACCESS_URL = 'https://streamnative.io/lps/early-access-program-for-streamnative';

const PreviewSlide = () => (
  <div className="slide-body preview-body">
    <div className="preview-left">
      <span className="preview-eyebrow">Private Preview</span>
      <h2 className="preview-title">Agent Engine — early access.</h2>
      <p className="preview-sub">
        Runtime, MCP &amp; AI Gateway, <strong>Agent Registry</strong>, and versioned storage — running on the
        streaming substrate you already know. Apply for the early access program.
      </p>
      <ul className="preview-list">
        <li>Hosted Runtime for Agents, Functions, and Connectors (Google ADK, OpenAI Agents SDK, custom harnesses)</li>
        <li>MCP &amp; AI Gateway — auth, rate limits, audit, schema enforcement</li>
        <li>Agent Registry — discoverable Agents, Sessions, Environments, Files, Vaults, Skills</li>
        <li>Versioned, branchable storage for memory &amp; sessions</li>
        <li>BYOC deployment — data stays in your cloud</li>
      </ul>
      <p className="preview-url">{EARLY_ACCESS_URL}</p>
    </div>
    <div className="preview-qr">
      <div className="qr-frame">
        <QRCodeSVG
          value={EARLY_ACCESS_URL}
          size={320}
          level="M"
          bgColor="transparent"
          fgColor="#202939"
          marginSize={0}
        />
      </div>
      <p className="qr-caption">Scan to apply</p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 20 — Close
   ══════════════════════════════════════════════════════════════════════ */

const CloseSlide = () => (
  <div className="slide-body s12-body">
    <h2 className="s12-h">Thank you.<br /><em>Questions?</em></h2>
    <dl className="s12-meta">
      <dt>Speaker</dt><dd>Sijie Guo</dd>
      <dt>Email</dt><dd><a href="mailto:sg@streamnative.io">sg@streamnative.io</a></dd>
      <dt>Handle</dt><dd>@sijieg</dd>
      <dt>Company</dt><dd>StreamNative · streamnative.io</dd>
    </dl>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide order
   ══════════════════════════════════════════════════════════════════════ */

export const slides: readonly Slide[] = [
  {
    id: 1, theme: 'beige',
    section: 'Streaming the Future · Sijie Guo',
    notes: [
      "I've spent 10+ years building streaming infrastructure. Last 18 months watching enterprise teams put AI agents into production.",
      "A pattern keeps showing up — most of what they're building IS streaming infrastructure, rebranded. That's the talk.",
    ],
    render: TitleSlide,
  },
  {
    id: 2, theme: 'beige',
    section: 'About StreamNative',
    notes: [
      'Quick context before we dig in — StreamNative is the Lakestream Platform for Apache Pulsar and Apache Kafka.',
      'Founded by the original Apache Pulsar creators. Ursa is our object-storage-native engine — elastic, multi-tenant, lakehouse-cost.',
      'BYOC so data stays in your cloud. Open standards in and out.',
      'And on top, the Orca Agent Engine — the "Agents" addition that\'s the subject of this talk.',
    ],
    render: AboutSlide,
  },
  {
    id: 3, theme: 'beige',
    section: 'Setup · The Gap',
    notes: [
      "Set the stakes. If your agent pilot stalled, you're not alone.",
      "The interesting question is why — and the answer is rarely 'we need a better model.' It's plumbing — the harness around the model.",
    ],
    render: GapSlide,
  },
  {
    id: 4, theme: 'beige',
    section: 'Framing · Nine Challenges, Three Pillars',
    notes: [
      'Pause here. Read the three pillar names aloud — Context · Trust · Coordination.',
      'Nine concrete challenges underneath. The pillars are what they should remember; the challenges are what they will recognize from their own work.',
    ],
    render: FrameworkSlide,
  },
  {
    id: 5, theme: 'beige',
    section: 'Framing · Thesis',
    notes: [
      'Streaming covers roughly 80–90% of each pillar.',
      "For the next nine slides I walk each challenge and call out — streaming problem yes or no? When no, who closes the gap.",
    ],
    render: ThesisSlide,
  },
  {
    id: 6, theme: 'beige',
    section: 'Pillar 01 · Context · Building & Maintenance',
    notes: [
      "Easiest sell — streaming ETL rebranded for AI. Multi-modal is the new wrinkle; nightly batch wasn't built for CRM, transcripts, and screenshots flowing together.",
      'Fan-out from a single substrate into vector, OLAP, feature, object stores.',
    ],
    render: C1_Building,
  },
  {
    id: 7, theme: 'beige',
    section: 'Pillar 01 · Context · Querying',
    notes: [
      "Querying is the exception — retrieval is fan-in at request time, not a streaming flow.",
      "RisingWave and Flink SQL turn the stream into queryable state. Yingjun is in the room — RisingWave is our partner closing this gap natively.",
    ],
    render: C2_Querying,
  },
  {
    id: 8, theme: 'beige',
    section: 'Pillar 01 · Context · Memory, Session & State',
    notes: [
      "Agents are not single-shot — they explore hypotheses. Try plan A, abandon, try plan B, branch off plan A at step 3 and try a different tool.",
      "That demands branchable infrastructure. Append-only logs are versioned by construction; branching is a pointer copy.",
    ],
    render: C3_Memory,
  },
  {
    id: 9, theme: 'beige',
    section: 'Pillar 02 · Trust · Auditing',
    notes: [
      'LLMs are opaque. Only audit signal is the I/O trace — full payloads, not metadata.',
      "The diagram: agent loops with LLM and tool. Every exchange is tee'd into a streaming audit log. Streaming serves it; object-store tiering handles volume.",
      "Auditor asks 'why did this agent recommend X?' — if all you have is metrics, you're done.",
    ],
    render: C4_Auditing,
  },
  {
    id: 10, theme: 'beige',
    section: 'Pillar 02 · Trust · Replay & Validation',
    notes: [
      'Leverage point — same log proves compliance AND drives regression tests.',
      "Change a prompt, upgrade a model — replay yesterday's traffic, diff outputs. Two outcomes from one piece of infrastructure.",
    ],
    render: C5_Replay,
  },
  {
    id: 11, theme: 'beige',
    section: 'Pillar 02 · Trust · Governance',
    notes: [
      "Governance isn't a separate product. It's built into every layer that touches data.",
      "But streaming is the connective layer, so it's the natural chokepoint. Source-side enforcement = death by 1,000 cuts.",
      "Same logic as 'authn at the API gateway, not in every service.'",
    ],
    render: C6_Governance,
  },
  {
    id: 12, theme: 'beige',
    section: 'Pillar 02 · Trust · Auth',
    notes: [
      'Auth needs a centralized gateway. Streaming carries messages; auth needs its own boundary.',
      'Closed by MCP & AI Gateway in the Agent Engine.',
    ],
    render: C7_Auth,
  },
  {
    id: 13, theme: 'beige',
    section: 'Pillar 03 · Coordination · Routing',
    notes: [
      'Two compute models. Agents are LLM-backed — smart actions, ambiguous inputs, expensive. Functions are deterministic code — μs latency, high throughput, predictable.',
      'A content-based router on the streaming layer sends each event to the right one. Same substrate, two execution profiles.',
    ],
    render: C8_Routing,
  },
  {
    id: 14, theme: 'beige',
    section: 'Pillar 03 · Coordination · Multi-agent',
    notes: [
      "Multi-agent systems start as N-by-N point-to-point — that doesn't scale.",
      "Each agent connects ONCE to a shared event bus. They publish domain events and subscribe to what they need.",
      "Same patterns that fixed microservices: decoupling, durability, fan-in/fan-out, backpressure.",
    ],
    render: C9_MultiAgent,
  },
  {
    id: 15, theme: 'beige',
    section: 'Completion · Honest Caveat',
    notes: [
      "Streaming alone doesn't ship an agent platform. Four gaps. Be honest — or you'll lose the room.",
    ],
    render: CaveatSlide,
  },
  {
    id: 16, theme: 'beige',
    section: 'Completion · Partners + Agent Engine',
    notes: [
      "Querying — RisingWave and Flink SQL. Partners. They do this natively.",
      'Auth via MCP & AI Gateway. Runtime hosts Agent Functions (Google ADK, OpenAI Agents SDK, custom). Registry discovers resources. Versioned storage productizes the log.',
    ],
    render: ClosersSlide,
  },
  {
    id: 17, theme: 'beige',
    section: 'Platform · Layers + Interop',
    notes: [
      "Two layers, one platform. Orca Agent Engine on top — Runtime, Registry, MCP & AI Gateway, versioned memory. Data Streaming underneath — Connect, Stream, Query.",
      "Interop callouts matter. Agent Engine: MCP, A2A, OpenAI-compatible, Google ADK, OTel. Data Streaming: Kafka API, Pulsar, SQL via Flink and RisingWave, Iceberg.",
      "Governance threads through every layer — BYOC, RBAC, audit, observability, lineage. Not a separate column; it's everywhere.",
    ],
    render: PlatformSlide,
  },
  {
    id: 18, theme: 'beige',
    section: 'Mental Model',
    notes: [
      'One sentence — enterprise agent infrastructure is mostly streaming infrastructure in disguise.',
      "Three pillars. Architect for the substrate, not for the symptoms.",
    ],
    render: MantraSlide,
  },
  {
    id: 19, theme: 'beige',
    section: 'Get Early Access',
    notes: [
      'Orca Agent Engine is in private preview. Scan the QR to apply.',
      'We work with you to onboard your agents — pick a real workflow, ship it on the platform, give us feedback.',
    ],
    render: PreviewSlide,
  },
  {
    id: 20, theme: 'beige',
    section: 'Close · Q&A',
    notes: [
      'Thank you. Open the floor.',
      'Likely questions — streaming vs API-gateway-only, replay latency budget, full-payload logging cost. Two-sentence answers ready.',
    ],
    render: CloseSlide,
  },
] as const;
